import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type {
  StoryHistoryEntry,
  StoryHistoryEntryInput,
  StoryHistoryQuery,
  StoryHistoryState,
} from '@/IsekaiRPG/modules/history/historyTypes';
import { StoryHistoryEntryInputSchema, StoryHistoryStateSchema } from '@/IsekaiRPG/modules/history/historyTypes';
import { compareStoryHistoryTimeline } from '@/IsekaiRPG/modules/history/historyTimeline';

// ============================================
// 故事历史 Store
// 作为统一主历史的基础容器，负责时间线条目的增删改查。
// 当前阶段先独立于旧消息系统存在，后续由各分支系统逐步接入。
// ============================================

function createEntryId(): string {
  return `story_history_${Date.now()}_${Math.floor(Math.random() * 100000)}`;
}

function normalizeEntryInput(input: StoryHistoryEntryInput): StoryHistoryEntry {
  const parsed = StoryHistoryEntryInputSchema.parse(input);
  return {
    ...parsed,
    id: parsed.id || createEntryId(),
    timestamp: parsed.timestamp ?? Date.now(),
  };
}

function sortEntries(entries: StoryHistoryEntry[]): StoryHistoryEntry[] {
  return [...entries].sort(compareStoryHistoryTimeline);
}

function applyMaxEntries(entries: StoryHistoryEntry[], maxEntries: number): StoryHistoryEntry[] {
  if (entries.length <= maxEntries) return entries;
  return entries.slice(entries.length - maxEntries);
}

export const useStoryHistoryStore = defineStore('storyHistory', () => {
  const state = ref<StoryHistoryState>(
    StoryHistoryStateSchema.parse({
      entries: [],
    }),
  );

  const entries = computed(() => state.value.entries);

  function setMaxEntries(maxEntries: number): void {
    state.value.maxEntries = Math.max(1, Math.trunc(maxEntries));
    state.value.entries = applyMaxEntries(sortEntries(state.value.entries), state.value.maxEntries);
  }

  function replaceEntries(nextEntries: StoryHistoryEntryInput[]): void {
    const normalized = nextEntries.map(entry => normalizeEntryInput(entry));
    state.value.entries = applyMaxEntries(sortEntries(normalized), state.value.maxEntries);
  }

  function appendEntry(input: StoryHistoryEntryInput): StoryHistoryEntry {
    const created = normalizeEntryInput(input);
    const nextEntries = [...state.value.entries, created];
    state.value.entries = applyMaxEntries(sortEntries(nextEntries), state.value.maxEntries);
    return created;
  }

  function appendEntries(inputs: StoryHistoryEntryInput[]): StoryHistoryEntry[] {
    const created = inputs.map(input => normalizeEntryInput(input));
    const nextEntries = [...state.value.entries, ...created];
    state.value.entries = applyMaxEntries(sortEntries(nextEntries), state.value.maxEntries);
    return created;
  }

  function getEntry(entryId: string): StoryHistoryEntry | null {
    return state.value.entries.find(entry => entry.id === entryId) ?? null;
  }

  function updateEntry(
    entryId: string,
    patch: Partial<Omit<StoryHistoryEntry, 'id'>>,
    mergeMetadata: boolean = true,
  ): boolean {
    const index = state.value.entries.findIndex(entry => entry.id === entryId);
    if (index === -1) return false;

    const current = state.value.entries[index];
    const next: StoryHistoryEntry = {
      ...current,
      ...patch,
      metadata:
        patch.metadata === undefined
          ? current.metadata
          : mergeMetadata
            ? { ...(current.metadata || {}), ...patch.metadata }
            : patch.metadata,
    };

    const nextEntries = [...state.value.entries];
    nextEntries[index] = next;
    state.value.entries = sortEntries(nextEntries);
    return true;
  }

  function updateEntryContent(entryId: string, content: string): boolean {
    return updateEntry(entryId, { content }, false);
  }

  function updateEntryMetadata(entryId: string, metadata: Record<string, unknown>, merge: boolean = true): boolean {
    return updateEntry(entryId, { metadata }, merge);
  }

  function removeEntry(entryId: string): boolean {
    const before = state.value.entries.length;
    state.value.entries = state.value.entries.filter(entry => entry.id !== entryId);
    return state.value.entries.length !== before;
  }

  function clearHistory(): void {
    state.value.entries = [];
  }

  function queryEntries(query: StoryHistoryQuery = {}): StoryHistoryEntry[] {
    const { sessionKey, sceneName, sourceType, role, participantId, keyword, sinceTimestamp, untilTimestamp, limit } =
      query;

    let result = state.value.entries.filter(entry => {
      if (sessionKey && entry.sessionKey !== sessionKey) return false;
      if (sceneName && entry.sceneName !== sceneName) return false;
      if (sourceType && entry.sourceType !== sourceType) return false;
      if (role && entry.role !== role) return false;
      if (participantId && !entry.participantIds.includes(participantId)) return false;
      if (sinceTimestamp !== undefined && entry.timestamp < sinceTimestamp) return false;
      if (untilTimestamp !== undefined && entry.timestamp > untilTimestamp) return false;
      if (keyword && !entry.content.includes(keyword)) return false;
      return true;
    });

    result = sortEntries(result);

    if (limit && result.length > limit) {
      result = result.slice(result.length - limit);
    }

    return result;
  }

  function getRecentEntries(limit: number): StoryHistoryEntry[] {
    return queryEntries({ limit });
  }

  return {
    state,
    entries,
    setMaxEntries,
    replaceEntries,
    appendEntry,
    appendEntries,
    getEntry,
    updateEntry,
    updateEntryContent,
    updateEntryMetadata,
    removeEntry,
    clearHistory,
    queryEntries,
    getRecentEntries,
  };
});
